import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, Plus, Loader2, LayoutGrid, Trash2, Columns as ColumnsIcon, Rows2, Columns3 } from 'lucide-react';
// Recharts imports removed - now using UniversalChartRenderer
import { DashboardService } from '../services/dashboardService';
import { ChartService } from '../services/chartService';
import { useAuth } from '../contexts/AuthContext';
import { currentOrganizationAtom } from '../store/organizationAtoms';
import { currentDashboardAtom, refreshDashboardsAtom } from '../store/dashboardAtoms';
import type { ChartData } from '../store/chartAtoms';
import type { DashboardChartItem } from '@shared/types/dashboard.types';
import type { Chart, ChartStudy, ChartType, UpdateChart, AxisOptions, ChartConfiguration } from '@shared/schemas/chart';
import AddChartToDashboardModal from '../components/dashboard/AddChartToDashboardModal';
import DraggableDashboardChart from '../components/dashboard/DraggableDashboardChart';
import ChartStudiesDialog from '../components/charts/ChartStudiesDialog';
import EditableChartTitle from '../components/charts/EditableChartTitle';
import UniversalChartRenderer from '../components/charts/UniversalChartRenderer';
import { normalizeChartData } from '../lib/chartDataNormalizer';
import { convertDatabaseChartToUniversal } from '@shared/schemas/chart';
import { useCustomPalettes, useActiveDefaultPaletteId } from '../contexts/ChartPreferencesContext';
import { useTheme } from '../lib/themeUtils';

// Re-add DndContext and related imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

interface DisplayableDashboardChart extends Chart {
  order: number;
}

const DashboardViewPage: React.FC = () => {
  const { organizationId, dashboardId } = useParams<{ organizationId: string; dashboardId: string }>();
  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);
  const [currentDashboard, setCurrentDashboard] = useAtom(currentDashboardAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [, setRefreshDashboards] = useAtom(refreshDashboardsAtom);
  const [isAddChartModalOpen, setIsAddChartModalOpen] = useState(false);
  const customPalettes = useCustomPalettes();
  const isDarkMode = useTheme();
  const activeDefaultPaletteId = useActiveDefaultPaletteId();

  const [displayedCharts, setDisplayedCharts] = useState<DisplayableDashboardChart[]>([]);
  const [chartsLoading, setChartsLoading] = useState<boolean>(false);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchDashboardData = useCallback(async () => {
    if (currentUser && organizationId && dashboardId) {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedDashboard = await DashboardService.getDashboard(
          organizationId,
          currentUser.uid,
          dashboardId
        );
        if (fetchedDashboard) {
          setCurrentDashboard(fetchedDashboard);
        } else {
          setError('Dashboard not found.');
          setCurrentDashboard(null);
        }
      } catch (err) {
        console.error('Error fetching dashboard details:', err);
        setError('Failed to load dashboard details.');
        setCurrentDashboard(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setCurrentDashboard(null);
      setIsLoading(false);
      if (!organizationId || !dashboardId) {
        setError("Invalid dashboard link.");
      }
    }
  }, [currentUser, organizationId, dashboardId, setCurrentDashboard]);

  useEffect(() => {
    if (organizationId && currentOrganization && organizationId !== currentOrganization.id) {
        console.warn('Organization ID from URL differs from current context.');
    }
    fetchDashboardData();
    return () => {
      setCurrentDashboard(null); 
    };
  }, [fetchDashboardData, organizationId, currentOrganization]);

  useEffect(() => {
    const fetchFullChartData = async () => {
      if (!currentDashboard || !currentUser || !organizationId || currentDashboard.charts.length === 0) {
        setDisplayedCharts([]);
        return;
      }
      setChartsLoading(true);
      try {
        const chartPromises = currentDashboard.charts.map(item => 
          ChartService.getById(organizationId, currentUser.uid, item.chartId)
            .then(chart => chart ? ({ 
              ...chart, 
              order: item.order 
            } as DisplayableDashboardChart) : null)
        );
        const fetchedChartsData = (await Promise.all(chartPromises)).filter(Boolean) as DisplayableDashboardChart[];
        setDisplayedCharts(fetchedChartsData.sort((a, b) => a.order - b.order));
      } catch (err) {
        console.error('Error fetching full chart data for dashboard:', err);
        setError('Failed to load some charts for the dashboard.');
        setDisplayedCharts([]);
      } finally {
        setChartsLoading(false);
      }
    };

    fetchFullChartData();
  }, [currentDashboard, currentUser, organizationId]);

  const handleDeleteDashboard = async () => {
    if (!currentUser || !organizationId || !dashboardId || !currentDashboard) return;
    if (window.confirm(`Are you sure you want to delete dashboard: ${currentDashboard.title}?`)) {
        try {
            await DashboardService.deleteDashboard(organizationId, currentUser.uid, dashboardId);
            setRefreshDashboards(prev => prev + 1); 
            navigate(`/dashboards`); 
        } catch (err) {
            console.error('Error deleting dashboard:', err);
            setError('Failed to delete dashboard.');
        }
    }
  };

  const handleOpenAddChartModal = () => {
    if (currentDashboard) {
      setIsAddChartModalOpen(true);
    } else {
      console.error('Cannot open Add Chart modal: currentDashboard is null or undefined.');
    }
  };

  const handleAddChartsToDashboard = async (selectedChartIds: string[]) => {
    if (!currentUser || !organizationId || !dashboardId || !currentDashboard) {
      console.error('Cannot add charts: Missing context or dashboard data.');
      return;
    }
    const currentChartCount = currentDashboard.charts.length;
    const newChartItems: DashboardChartItem[] = selectedChartIds.map((chartId, index) => ({
      chartId,
      order: currentChartCount + index, 
    }));
    try {
      for (const item of newChartItems) {
        await DashboardService.addChartToDashboard(organizationId, currentUser.uid, dashboardId, item);
      }
      await fetchDashboardData();
    } catch (err) {
      console.error('Error adding charts to dashboard:', err);
      setError('Failed to add selected charts to the dashboard.');
    }
  };

  const handleRemoveChartFromDashboard = async (chartIdToRemove: string) => {
    console.log('DashboardViewPage: handleRemoveChartFromDashboard called with chartIdToRemove:', chartIdToRemove);
    if (!currentUser || !organizationId || !dashboardId || !currentDashboard) {
      console.error('DashboardViewPage: Cannot remove chart, missing context.');
      return;
    }
    try {
      await DashboardService.removeChartFromDashboard(organizationId, currentUser.uid, dashboardId, chartIdToRemove);
      setCurrentDashboard(prev => prev ? ({ 
        ...prev, 
        charts: prev.charts.filter(c => c.chartId !== chartIdToRemove)
      }) : null);
      console.log('DashboardViewPage: Chart removed from currentDashboard state.');
    } catch (error) {
      console.error('DashboardViewPage: Error removing chart from dashboard:', error);
      setError('Failed to remove chart.');
    }
  };

  const handleUpdateLayout = async (newColumnCount: 1 | 2 | 3) => {
    if (!currentUser || !organizationId || !dashboardId || !currentDashboard) return;

    // Optimistically update UI for responsiveness, though fetchDashboardData will confirm
    // setCurrentDashboard(prev => prev ? ({ ...prev, layoutConfiguration: { ...prev.layoutConfiguration, columns: newColumnCount } }) : null);

    try {
      await DashboardService.updateDashboardLayout(organizationId, currentUser.uid, dashboardId, { columns: newColumnCount });
      await fetchDashboardData(); // Re-fetch to confirm and get latest updatedAt
    } catch (err) {
      console.error('Error updating dashboard layout:', err);
      setError('Failed to update layout.');
      // Optionally revert optimistic update here if needed
    }
  };

  const handleOpenChartStudiesDialog = (chartToOpen: Chart) => {
    console.log('[DashboardViewPage] handleOpenChartStudiesDialog - chartToOpen.configuration BEFORE setting state:', JSON.parse(JSON.stringify(chartToOpen?.configuration)));
    setStudiesDialogState({
      isOpen: true,
      chart: chartToOpen,
      isDatabase: true,
    });
  };

  const [studiesDialogState, setStudiesDialogState] = useState<{
    isOpen: boolean;
    chart: Chart | null;
    isDatabase: boolean;
  }>({ isOpen: false, chart: null, isDatabase: true });

  const handleApplyStudiesToChartFromDialog = async (
    originalChart: Chart, 
    updatedStudies: ChartStudy[], 
    newChartType: ChartType, 
    newAxisOptions: AxisOptions | null, 
    newDisplayOptions: { showGridLines: boolean } | null,
    updatedConfiguration: Partial<ChartConfiguration>
  ) => {
    if (!currentUser || !organizationId || !originalChart || !originalChart.id) {
      setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true });
      return;
    }
    console.log('Applying studies, type, and color config to chart:', originalChart.id, updatedStudies, newChartType, newAxisOptions, newDisplayOptions, updatedConfiguration);
    try {
      // Start with the existing chart configuration and overlay the updates from the dialog
      const baseConfiguration = originalChart.configuration || {};
      const finalConfiguration: Partial<ChartConfiguration> = {
        ...baseConfiguration,
        ...updatedConfiguration, // This carries singleColor or colorPalette from the dialog preview state
      };

      // Ensure singleColor takes precedence as per our simplified logic
      if (finalConfiguration.singleColor) {
        delete finalConfiguration.colorPalette;
      }
      // Clean up undefined singleColor if it somehow became undefined (e.g. user cleared it)
      if (finalConfiguration.singleColor === undefined) {
        delete finalConfiguration.singleColor;
      }
       // Clean up undefined colorPalette as well
      if (finalConfiguration.colorPalette === undefined) {
        delete finalConfiguration.colorPalette;
      }

      const payload: UpdateChart = {
        studies: updatedStudies,
        type: newChartType,
        configuration: finalConfiguration, // Pass the merged and cleaned configuration
      };
      if (newAxisOptions !== null) {
        payload.axisOptions = newAxisOptions;
      }
      if (newDisplayOptions !== null) {
        payload.displayOptions = newDisplayOptions;
      }

      await ChartService.update(organizationId, originalChart.userId, originalChart.id, payload);
      
      await fetchDashboardData();
    } catch (error) {
      console.error('Error applying studies/type/axis options/color to chart from dashboard:', error);
    } finally {
      setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true });
    }
  };

  const renderStudiesPreviewChart = useCallback((chartToPreview: Chart | ChartData | any, studiesToApply: ChartStudy[]): JSX.Element | null => {
    console.log('[DashboardViewPage] renderStudiesPreviewChart - chartToPreview.configuration:', JSON.parse(JSON.stringify(chartToPreview?.configuration)));
    
    // Convert to universal format if it's a database chart
    let universalChart: ChartData;
    if (chartToPreview.datasets) {
      // It's a database chart, convert it
      universalChart = convertDatabaseChartToUniversal(chartToPreview);
    } else {
      // It's already a local/universal chart
      universalChart = chartToPreview;
    }
    
    const config = normalizeChartData(universalChart, { 
      height: 400,
      customPalettes,
      isDarkMode,
      globalDefaultPaletteId: activeDefaultPaletteId,
      enableAdvancedFeatures: true,
    });
    config.studies = studiesToApply;
    return <UniversalChartRenderer config={config} />;
  }, [customPalettes, isDarkMode, activeDefaultPaletteId]);

  // Re-add handleDragEnd
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && currentDashboard && currentUser && organizationId && dashboardId) {
      const oldIndex = displayedCharts.findIndex((c) => c.id === active.id);
      const newIndex = displayedCharts.findIndex((c) => c.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedDisplayCharts = arrayMove(displayedCharts, oldIndex, newIndex);
      setDisplayedCharts(reorderedDisplayCharts);

      const reorderedDashboardChartItems: DashboardChartItem[] = reorderedDisplayCharts.map((chart, index) => ({
        chartId: chart.id,
        order: index, 
      }));
      
      setCurrentDashboard(prev => prev ? ({ ...prev, charts: reorderedDashboardChartItems }) : null);

      try {
        await DashboardService.updateDashboardCharts(organizationId, currentUser.uid, dashboardId, reorderedDashboardChartItems);
      } catch (err) {
        console.error('Error updating chart order:', err);
        setError('Failed to save new chart order. Reverting UI.');
        fetchDashboardData(); // Revert UI by re-fetching original order
      }
    }
  };

  // Callback for when chart data is updated via the editor
  const handleChartDataUpdateOnDashboardPage = () => {
    console.log('DashboardViewPage.tsx: Chart data updated, re-fetching dashboard data.');
    fetchDashboardData(); // This existing function already re-fetches and updates state
  };

  const handleUpdateDashboardTitle = async (newTitle: string) => {
    if (!currentUser || !organizationId || !dashboardId || !currentDashboard) {
      console.error('Cannot update dashboard title: Missing context or dashboard data.');
      // Optionally, show an error to the user
      return;
    }
    if (newTitle === currentDashboard.title) {
      return; // No change, no need to update
    }

    try {
      await DashboardService.updateDashboard(organizationId, currentUser.uid, dashboardId, { title: newTitle });
      // Optimistically update the title in the local state for immediate feedback
      setCurrentDashboard(prev => prev ? { ...prev, title: newTitle } : null);
      // No need to call fetchDashboardData() if only the title changed and local state is updated
      // However, if other parts of the dashboard could change or to ensure consistency:
      // await fetchDashboardData(); 
      setRefreshDashboards(prev => prev + 1); // if you rely on this for other parts of the app to refresh
    } catch (err) {
      console.error('Error updating dashboard title:', err);
      setError('Failed to update dashboard title.');
      // Optionally, revert optimistic update if save fails
      // fetchDashboardData(); // to revert to the server state
    }
  };

  if (isLoading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </Sidebar>
    );
  }

  if (error && !currentDashboard) {
    return (
      <Sidebar>
        <div className="space-y-6">
            <Button variant="outline" onClick={() => navigate('/dashboards')} className="mb-4">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Dashboards
            </Button>
            <Card className="text-center">
                <CardHeader>
                    <CardTitle>Error</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-destructive">{error}</p>
                </CardContent>
            </Card>
        </div>
      </Sidebar>
    );
  }

  if (!currentDashboard) {
    return (
      <Sidebar>
         <div className="space-y-6">
            <Button variant="outline" onClick={() => navigate('/dashboards')} className="mb-4">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Dashboards
            </Button>
            <p className="text-center text-muted-foreground">Dashboard not available.</p>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4 border-b border-border pb-4 pt-0">
            <Button variant="outline" size="icon" onClick={() => navigate('/dashboards')} className="h-9 w-9 shrink-0">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            {currentDashboard && (
              <EditableChartTitle
                title={currentDashboard.title}
                onSave={handleUpdateDashboardTitle}
                textClassName="text-xl font-semibold text-foreground"
                inputClassName="text-xl font-semibold"
                // isLoading={isUpdatingTitle} // You might want to add a state for this
              />
            )}
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              <Button variant="outline" onClick={handleOpenAddChartModal}>
                <Plus className="h-4 w-4 mr-2" />
                Add Chart
              </Button>
              <div className="flex items-center gap-1 border border-border rounded-md p-0.5 bg-gray-100">
                {[1, 2, 3].map((cols) => {
                  const isActive = currentDashboard?.layoutConfiguration.columns === cols;
                  let IconComponent;
                  if (cols === 1) IconComponent = Rows2;
                  else if (cols === 2) IconComponent = ColumnsIcon;
                  else IconComponent = Columns3;
                  
                  return (
                    <Button
                      key={cols}
                      variant={isActive ? 'outline' : 'ghost'}
                      size="icon"
                      className={`h-7 w-7 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                      onClick={() => handleUpdateLayout(cols as 1 | 2 | 3)}
                      title={`${cols} Column${cols > 1 ? 's' : ''}`}
                    >
                      <IconComponent className="h-4 w-4" />
                    </Button>
                  );
                })}
              </div>
               <Button variant="destructive" size="icon" onClick={handleDeleteDashboard} title="Delete Dashboard" className="h-9 w-9 shrink-0 bg-white hover:bg-destructive/20">
                  <Trash2 className="h-5 w-5 text-destructive "/>
              </Button>
            </div>
          </div>

          {chartsLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading charts...</span>
            </div>
          )}

          {!chartsLoading && displayedCharts.length === 0 && (
            <div className="min-h-[300px] border-2 border-dashed border-muted rounded-lg flex items-center justify-center">
              <div className="text-center">
                <LayoutGrid className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-lg font-bold">This dashboard is empty.</p>
                <p className="text-xs text-muted-foreground">Click "Add Chart" to get started.</p>
              </div>
            </div>
          )}
          
          {!chartsLoading && currentDashboard && (
            <div>
              {chartsLoading && <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin"/></div>}
              
              {!chartsLoading && displayedCharts.length > 0 && (
                <>
                    <div className="flex justify-end text-xs text-muted-foreground">Click and drag to reorder charts</div>
                    <SortableContext items={displayedCharts.map(c => c.id)} strategy={rectSortingStrategy}>
                    <div className={`mt-4 grid gap-6 grid-cols-1 md:grid-cols-${currentDashboard.layoutConfiguration.columns === 1 ? '1' : '2'} lg:grid-cols-${currentDashboard.layoutConfiguration.columns || 3}`}>
                        {displayedCharts.sort((a,b)=>a.order - b.order).map(chartInLoop => (
                            <DraggableDashboardChart 
                                key={chartInLoop.id} 
                                id={chartInLoop.id} 
                                chart={chartInLoop as DisplayableDashboardChart}
                                onRemoveChart={handleRemoveChartFromDashboard}
                                onOpenStudies={handleOpenChartStudiesDialog}
                                onChartDataUpdate={handleChartDataUpdateOnDashboardPage}
                                hideTitle={false} 
                                hideEditButton={true}
                            />
                        ))}
                    </div>
                  </SortableContext>
                </>
              )}
            </div>
          )}
          {error && <p className="text-destructive text-center mt-4">Error related to charts: {error}</p>}
        </div>

        {currentDashboard && (
          <AddChartToDashboardModal 
              isOpen={isAddChartModalOpen} 
              onClose={() => setIsAddChartModalOpen(false)} 
              onChartsSelected={handleAddChartsToDashboard}
              existingChartIdsOnDashboard={currentDashboard.charts.map(c => c.chartId)}
          />
        )}

        {studiesDialogState.isOpen && studiesDialogState.chart && (
          <ChartStudiesDialog
            isOpen={studiesDialogState.isOpen}
            onClose={() => setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true })}
            chart={studiesDialogState.chart}
            isDatabaseChart={studiesDialogState.isDatabase}
            initialStudies={studiesDialogState.chart.studies || []}
            onApplyStudies={async (updatedStudiesFromDialog, updatedTypeFromDialog, updatedAxisOptionsFromDialog, updatedDisplayOptionsFromDialog, updatedConfigurationFromDialog) => {
              if (studiesDialogState.chart) {
                await handleApplyStudiesToChartFromDialog(
                  studiesDialogState.chart, 
                  updatedStudiesFromDialog, 
                  updatedTypeFromDialog, 
                  updatedAxisOptionsFromDialog, 
                  updatedDisplayOptionsFromDialog,
                  updatedConfigurationFromDialog
                );
              }
            }}
            currentUser={currentUser}
            currentOrganization={currentOrganization}
            renderPreviewFunction={renderStudiesPreviewChart}
          />
        )}
      </DndContext>
    </Sidebar>
  );
};

export default DashboardViewPage; 